import React, { useEffect, useState } from "react"
import ReactMarkdown from "react-markdown"
import { Link, useLocation } from "react-router-dom"

import { Loading } from "components"
import useT from "utils/hooks/useT"

import { loadPage, rewriteImagePath } from "./helpUtils"

import "./HelpPage.scss"

// react-markdown does not emit raw HTML unless rehype-raw is added; we
// intentionally omit rehype-raw so user-authored markdown cannot inject
// script tags. If rehype-raw is added later, wrap the output with
// DOMPurify before rendering.
function slugify(text) {
  return String(text)
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
}

function flattenText(node) {
  if (node === null || node === undefined) return ""
  if (typeof node === "string" || typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(flattenText).join("")
  if (node.props && node.props.children) return flattenText(node.props.children)
  return ""
}

function makeHeading(Tag, seenSlugs) {
  return function Heading({ children, ...props }) {
    const base = slugify(flattenText(children))
    let slug = base
    let n = 2
    while (seenSlugs.has(slug)) {
      slug = `${base}-${n}`
      n++
    }
    seenSlugs.add(slug)
    return (
      <Tag id={slug} {...props}>
        {children}
      </Tag>
    )
  }
}

const MARKDOWN_COMPONENTS_BASE = {
  a: ({ href, children, ...props }) => {
    if (href && href.startsWith("/help/")) {
      return (
        <Link to={href} {...props}>
          {children}
        </Link>
      )
    }
    if (href && /^https?:/i.test(href)) {
      return (
        <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
          {children}
        </a>
      )
    }
    return (
      <a href={href} {...props}>
        {children}
      </a>
    )
  },
}

export default function HelpPage({ area, slug }) {
  const { t } = useT("common")
  const location = useLocation()
  const [markdown, setMarkdown] = useState(null)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    setMarkdown(null)
    setError(null)
    loadPage(area, slug)
      .then((md) => {
        if (!cancelled) setMarkdown(md)
      })
      .catch((err) => {
        if (!cancelled) setError(err)
      })
    return () => {
      cancelled = true
    }
  }, [area, slug])

  useEffect(() => {
    if (markdown === null) return
    let cancelled = false
    const scrollContainer = document.querySelector(".HelpScreen-content")
    if (!location.hash) {
      if (scrollContainer) scrollContainer.scrollTop = 0
      return
    }
    const id = decodeURIComponent(location.hash.slice(1))
    let attempts = 0
    const timers = []

    function scrollOnce(behavior) {
      if (cancelled) return false
      const el = document.getElementById(id)
      if (!el) return false
      if (scrollContainer) {
        const offset = el.getBoundingClientRect().top -
          scrollContainer.getBoundingClientRect().top +
          scrollContainer.scrollTop
        scrollContainer.scrollTo({ top: offset - 12, behavior })
      } else {
        el.scrollIntoView({ behavior, block: "start" })
      }
      return true
    }

    function tryScroll() {
      if (cancelled) return
      if (scrollOnce("smooth")) {
        // Re-anchor a few times after images finish loading; otherwise a
        // big help page with many <img> elements can shift layout below
        // the heading right after we scrolled to it.
        for (const delay of [350, 800, 1500]) {
          timers.push(setTimeout(() => scrollOnce("auto"), delay))
        }
        return
      }
      // Heading nodes may not be in the DOM yet on the first render of
      // a large page. Retry on each animation frame for up to ~800ms.
      if (attempts++ < 50) requestAnimationFrame(tryScroll)
    }
    requestAnimationFrame(tryScroll)
    return () => {
      cancelled = true
      for (const t of timers) clearTimeout(t)
    }
  }, [markdown, location.hash, area, slug])

  if (error) {
    return (
      <div className="HelpPage HelpPage-error">
        <h1>{t("helpPageNotFound")}</h1>
        <p>
          <code>{area}/{slug}</code>
        </p>
      </div>
    )
  }

  if (markdown === null) {
    return (
      <div className="HelpPage HelpPage-loading">
        <Loading />
      </div>
    )
  }

  const seenSlugs = new Set()
  const components = {
    ...MARKDOWN_COMPONENTS_BASE,
    h1: makeHeading("h1", seenSlugs),
    h2: makeHeading("h2", seenSlugs),
    h3: makeHeading("h3", seenSlugs),
    h4: makeHeading("h4", seenSlugs),
    h5: makeHeading("h5", seenSlugs),
    h6: makeHeading("h6", seenSlugs),
    img: ({ src, alt, ...props }) => (
      <img
        src={rewriteImagePath(src, area, slug)}
        alt={alt || ""}
        loading="lazy"
        {...props}
      />
    ),
  }

  return (
    <article className="HelpPage">
      <ReactMarkdown components={components}>{markdown}</ReactMarkdown>
    </article>
  )
}
